import '../../i18n';
import {useTranslation} from 'react-i18next';
import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';

import {
    FiMoreVertical,
    FiFolder,
    FiBell,
    FiCheckSquare,
    FiMinusSquare,
    FiStar,
    FiEdit2,
    FiCopy,
    FiSearch,
    FiFileText,
    FiImage,
    FiDownload,
    FiCornerUpLeft,
    FiRotateCcw,
    FiXCircle,
    FiTrash2,
    FiCheck,
    FiMessageSquare,
    FiSettings
} from 'react-icons/fi';
import { LuMessageSquareShare } from 'react-icons/lu';
import MessageCopyMenu from './MessageCopyMenu';
import CascadingSubmenu from './CascadingSubmenu';

import { PALE_WHITE_1, PALE_WHITE_2, PALE_WHITE_4, WHITE } from '../../appearance/Colors';

import { useMutation, useQuery } from '@apollo/client';
import { cloudinaryCore, isCloudinaryMedia } from '../../util/cloudinary';

import useTrashMessagesMutation from '../../hooks/useTrashMessagesMutation';

import useRestoreMessagesMutation from '../../hooks/useRestoreMessagesMutation';
import useUpdateCheckboxes from '../../hooks/useUpdateCheckboxes';
import useUpdateStarred from '../../hooks/useUpdateStarred';
import useNotificationSystem from '../../components/Notifications/useNotificationSystem';

import { AuthContext } from '../../util/context';

import { QUERY, MUTATION } from '../../gql-operations';

import { usePopper } from 'react-popper';

import customAnalytics from '../../util/custom-analytics';
import { setOpenMenuMessage, getOpenMenuMessage, addMenuListener } from './DefaultMessage';
import { downloadBlobInBrowser, isTauriEnvironment, saveBlobWithTauri } from '../../util/downloadHelpers';
import { resolveDownloadFileName } from '../../util/downloadFileName';
import { getSearchUrl, getStoredSearchEngine } from '../../constants/searchEngines';
import { openSettingsModal, SETTINGS_MODAL_TAB } from '../../util/settingsModalEvents';
import { getReminderDueTimezone, REMINDER_WEB_INSTALLATION_ID } from '../../util/reminderClientContext';
import DeleteModal from '../Modals/DeleteModal';
import ReminderPickerModal from '../Modals/ReminderPickerModal';
import {
    DropDownMenu,
    MESSAGE_MENU_Z_INDEX,
    SeparatorLI,
    Element,
    MenuItemLabel,
    MenuItemIcon,
    MenuItemSecondaryAction,
    DestructiveLabel
} from './MessageContextMenu';

const MessageShareModal = React.lazy(() => import('../Modals/MessageShareModal'));

const isServerBackedMessageId = id => /^[a-fA-F0-9]{24}$/.test(String(id || ''));

const getMutationMessageId = message => (
    message?._resolvedServerId ||
    message?.rawMessage?._resolvedServerId ||
    (
        isServerBackedMessageId(message?.rawMessage?.id)
            ? message.rawMessage.id
            : null
    ) ||
    message?.id
);

function MessageDropdown({
    message,
    index,
    hovering,
    showDeleteModal,
    hide,
    disabled = false,
    showMoveMessageModal,
    openMenu,
    screenName,
    toolbarTopOverride,
    toolbarRightAlign = false,
    compactMoreOnly = false,
    menuScope = 'default',
    onEditMessage,
    onSelectMessage,
    onReplyMessage,
    onShowInFolder,
    isSharedFolderView = false
}) {
    const {t: lnT} = useTranslation();

    const dropdownMenu = useRef(null);
    const menuKey = `${menuScope}:${message.id}`;

    const [showDropMenu, setShowDropMenu] = useState(false);
    const [activeMenuKey, setActiveMenuKey] = useState(getOpenMenuMessage);
    const [showReminderTrashWarning, setShowReminderTrashWarning] = useState(false);
    const [showReminderPicker, setShowReminderPicker] = useState(false);
    const [showSharing, setShowSharing] = useState(false);
    const { refetch: refetchDownloadFile } = useQuery(QUERY.DOWNLOAD_FILE, { skip: true });
    const { data: meData } = useQuery(QUERY.ME, {
        skip: !showDropMenu,
        fetchPolicy: 'cache-first'
    });
    const isTauri = isTauriEnvironment();

    const saveUrl = async (url, filename) => {
        const response = await fetch(url);
        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }

        const blob = await response.blob();
        if (isTauri) {
            return saveBlobWithTauri(blob, filename);
        }

        downloadBlobInBrowser(blob, filename);
        return true;
    };
    const mutationMessageId = getMutationMessageId(message);
    const canMutateMessage = isServerBackedMessageId(mutationMessageId);
    const effectiveReminderTimestamp = React.useMemo(() => {
        return message?.reminderAt || message?.reminderFiredAt || null;
    }, [lnT, message?.reminderAt, message?.reminderFiredAt]);

    const closeMenu = event => {
        // use contains only if you want not to close when clicking on an option
        // if (dropdownMenu.current && !dropdownMenu.current.contains(event.target)) {
        setOpenMenuMessage(null);
        setShowDropMenu(false);
        openMenu(false);
        document.removeEventListener('click', closeMenu);
        document.removeEventListener('keydown', closeOnEscape, true);
        // }
    };

    const closeMenuOnAction = () => {
        setOpenMenuMessage(null);
        setShowDropMenu(false);
        openMenu(false);
        document.removeEventListener('click', closeMenu);
        document.removeEventListener('keydown', closeOnEscape, true);
    };

    const closeOnEscape = event => {
        if (event.key === 'Escape') {
            // Let the submenu close first and restore focus to its parent row.
            if (event.target?.closest?.('[data-cascading-submenu], [data-submenu-open="true"]')) return;
            setOpenMenuMessage(null);
            setShowDropMenu(false);
            openMenu(false);
            document.removeEventListener('click', closeMenu);
            document.removeEventListener('keydown', closeOnEscape, true);
        }
    };

    // Track if this message's menu is open based on global state
    React.useEffect(() => {
        const listener = (previousId, currentId) => {
            setActiveMenuKey(currentId);
            if (currentId === menuKey) {
                setShowDropMenu(true);
            } else if (previousId === menuKey) {
                setShowDropMenu(false);
                openMenu(false);
                document.removeEventListener('click', closeMenu);
                document.removeEventListener('keydown', closeOnEscape, true);
            }
        };
        const removeListener = addMenuListener(listener);
        setActiveMenuKey(getOpenMenuMessage());
        // Initialize menu state on mount
        if (getOpenMenuMessage() === menuKey) {
            setShowDropMenu(true);
        }
        return removeListener;
    }, [menuKey, openMenu]);

    useEffect(() => () => {
        if (getOpenMenuMessage() === menuKey) setOpenMenuMessage(null);
    }, [menuKey]);

    useEffect(() => {
        if (!disabled) {
            return;
        }

        setOpenMenuMessage(null);
        setShowDropMenu(false);
        openMenu(false);
        document.removeEventListener('click', closeMenu);
        document.removeEventListener('keydown', closeOnEscape, true);
    }, [disabled]);

    const broadcastMessageUpdated = React.useCallback((updatedMessage) => {
        if (!updatedMessage?.id || typeof window === 'undefined') {
            return;
        }
        window.dispatchEvent(new CustomEvent('messageUpdated', { detail: { message: updatedMessage } }));
    }, [lnT]);

    const buildReminderNotificationId = React.useCallback((prefix) => {
        return `${prefix}-${message.id}-${Date.now()}`;
    }, [lnT, message.id]);

    const deferCacheSideEffects = React.useCallback((fn) => {
        if (typeof window !== 'undefined' && typeof window.requestAnimationFrame === 'function') {
            window.requestAnimationFrame(() => fn());
            return;
        }
        setTimeout(fn, 0);
    }, [lnT]);

    const [starMessage] = useMutation(MUTATION.STAR_MESSAGE, {
        optimisticResponse: {
            starMessage: {
                ...message,
                isStarred: new Date().toISOString(),
                updatedAt: new Date().toISOString(),
                __typename: 'Message'
            }
        },
        update: (store, { data: { starMessage } }) => {
            if (!starMessage) {
                return;
            }
            broadcastMessageUpdated(starMessage);
            deferCacheSideEffects(() => {
                updateStarred([starMessage]);
            });
        },
        refetchQueries: [
            {
                query: QUERY.GET_MESSAGE,
                variables: {
                    data: {
                        id: mutationMessageId
                    }
                }
            }
        ],
        onCompleted: res => {
            if (res) {
                customAnalytics.message_edit({
                    "action": "Star"
                }, message);
            }
        }
    });
    const [unstarMessage] = useMutation(MUTATION.UNSTAR_MESSAGE, {
        optimisticResponse: {
            unstarMessage: {
                ...message,
                isStarred: null,
                updatedAt: new Date().toISOString(),
                __typename: 'Message'
            }
        },
        update: (store, { data: { unstarMessage } }) => {
            if (!unstarMessage) {
                return;
            }
            broadcastMessageUpdated(unstarMessage);
            deferCacheSideEffects(() => {
                spliceStarred([unstarMessage]);
            });
        },
        refetchQueries: [
            {
                query: QUERY.GET_MESSAGE,
                variables: {
                    data: {
                        id: mutationMessageId
                    }
                }
            }
        ],
        onCompleted: res => {
            if (res) {
                customAnalytics.message_edit({
                    "action": "Unstar"
                }, message);
            }
        }
    });

    const [addCheckbox] = useMutation(MUTATION.ADD_CHECKBOX, {
        optimisticResponse: {
            addCheckbox: {
                ...message,
                isCheckbox: new Date().toISOString(),
                isChecked: null,
                updatedAt: new Date().toISOString(),
                __typename: 'Message'
            }
        },
        update: (store, { data: { addCheckbox } }) => {
            if (!addCheckbox) {
                return;
            }
            broadcastMessageUpdated(addCheckbox);
            deferCacheSideEffects(() => {
                updateCheckboxes([addCheckbox]);
            });
        },
        refetchQueries: [
            {
                query: QUERY.GET_MESSAGE,
                variables: {
                    data: {
                        id: mutationMessageId
                    }
                }
            }
        ],
        onCompleted: res => {
            if (res) {
                customAnalytics.message_edit({
                    'action': "Task"
                }, message);
            }
        }
    });

    const [removeCheckbox] = useMutation(MUTATION.REMOVE_CHECKBOX, {
        optimisticResponse: {
            removeCheckbox: {
                ...message,
                isCheckbox: null,
                isChecked: null,
                updatedAt: new Date().toISOString(),
                __typename: 'Message'
            }
        },
        update: (store, { data: { removeCheckbox } }) => {
            if (!removeCheckbox) {
                return;
            }
            broadcastMessageUpdated(removeCheckbox);
            deferCacheSideEffects(() => {
                spliceFromCheckboxes([removeCheckbox]);
            });
        },
        refetchQueries: [
            {
                query: QUERY.GET_MESSAGE,
                variables: {
                    data: {
                        id: mutationMessageId
                    }
                }
            }
        ],
        onCompleted: res => {
            if (res) {
                customAnalytics.message_edit({
                    'action': "Unask"
                }, message);
            }
        }
    });

    const [sendMessageToSlackMutation, { loading: sendMessageToSlackLoading }] = useMutation(MUTATION.SEND_MESSAGE_TO_SLACK);
    const [sendMessageToTelegramMutation, { loading: sendMessageToTelegramLoading }] = useMutation(MUTATION.SEND_MESSAGE_TO_TELEGRAM);
    const [copySharedMessageMutation, { loading: copySharedMessageLoading }] = useMutation(MUTATION.COPY_SHARED_MESSAGE);


    const [trashMessages] = useTrashMessagesMutation();

    const { restoreMessagesMutation } = useRestoreMessagesMutation();

    const { updateStarred, spliceStarred } = useUpdateStarred();


    const { updateCheckboxes, spliceFromCheckboxes } = useUpdateCheckboxes();

    const { showDefaultNotification, showCopyToClipboardNotification } = useNotificationSystem();
    const telegramIntegration = meData?.me?.integrations?.telegram || null;
    const slackIntegrations = meData?.me?.integrations?.slack || [];
    const preferredSlackIntegration = slackIntegrations[0] || null;
    const hasTextContent = [message?.title, message?.body].some(part => typeof part === 'string' && part.trim().length > 0);
    const hasImageMedia = !!message?.image;
    const hasFileMedia = !!message?.file;
    const hasForwardableContent = hasTextContent || hasImageMedia || hasFileMedia;
    const canSendToTelegram = !message?.deletedAt && hasForwardableContent && !!telegramIntegration?.id;
    const canSendToSlack = !message?.deletedAt && (hasTextContent || hasImageMedia) && !!preferredSlackIntegration;
    const canCopySharedMessage = isSharedFolderView && !message?.deletedAt && canMutateMessage;
    const canChooseTelegramMedia = canSendToTelegram && hasImageMedia && hasFileMedia;
    const formatReminderTimestamp = React.useCallback((value) => {
        const date = value ? new Date(value) : null;
        if (!date || Number.isNaN(date.getTime())) {
            return '';
        }

        return new Intl.DateTimeFormat(undefined, {
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        }).format(date);
    }, [lnT]);

    const normalizeReminderDeliveryType = React.useCallback(value => {
        const normalizedValue = String(value || '').trim().toLowerCase();
        return normalizedValue === 'email' ? 'EMAIL' : 'PUSH';
    }, [lnT]);

    const formatReminderDeliveryTypeLabel = React.useCallback(value => {
        return normalizeReminderDeliveryType(value) === 'EMAIL' ? 'email' : 'notification';
    }, [lnT, normalizeReminderDeliveryType]);

    const {
        state: {
            messagesToSend, selectedMessagesMap, selectedMessage, folderFilterType,
            searchFolderFilterType,
            messagesSearchInput,
            searchGlobal,
            searchDeletedMessages,
            showDeletedMessages
        },
        context: { setMessagesToSend, setSelectedMessagesMap, setShowMultiSelect, setSelectedMessage, searchTerm }
    } = React.useContext(AuthContext);
    const canReplyToMessage = !message?.deletedAt && typeof onReplyMessage === 'function';
    const canShowInFolder = !message?.deletedAt && typeof onShowInFolder === 'function' && Boolean(message?.folder);

    const showMenu = event => {
        event.preventDefault();
        event.stopPropagation();
        const willBeOpen = !showDropMenu;
        
        if (willBeOpen) {
            // Close any other open menu first
            const currentlyOpen = getOpenMenuMessage();
            if (currentlyOpen && currentlyOpen !== menuKey) {
                setOpenMenuMessage(null);
            }
            // Open this menu
            setOpenMenuMessage(menuKey);
            document.addEventListener('click', closeMenu);
            document.addEventListener('keydown', closeOnEscape, true);
        } else {
            // Close this menu
            setOpenMenuMessage(null);
            document.removeEventListener('click', closeMenu);
            document.removeEventListener('keydown', closeOnEscape, true);
        }
        setShowDropMenu(willBeOpen);
        openMenu(willBeOpen);
    };

    const copyTextToClipboard = async text => {
        if (!text || !text.trim()) {
            return;
        }

        try {
            await navigator.clipboard.writeText(text);
        } catch (error) {
            const textarea = document.createElement('textarea');
            textarea.value = text;
            textarea.style.position = 'fixed';
            textarea.style.top = '-9999px';
            textarea.style.left = '-9999px';
            document.body.appendChild(textarea);
            textarea.focus();
            textarea.select();
            document.execCommand('copy');
            document.body.removeChild(textarea);
        }

        showCopyToClipboardNotification();
    };

    const lookupMessageOnWeb = (event) => {
        event.stopPropagation();
        closeMenuOnAction();

        const lookupTerm = [message?.title, message?.body]
            .filter(part => typeof part === 'string' && part.trim().length > 0)
            .join('\n\n')
            .trim();

        if (!lookupTerm.length) {
            showDefaultNotification(`lookup-empty-${message.id}-${Date.now()}`, 'No text to look up');
            return;
        }

        try {
            const searchTerm = lookupTerm.length > 500 ? lookupTerm.slice(0, 500) : lookupTerm;
            const searchEngine = getStoredSearchEngine();
            const searchUrl = getSearchUrl(searchTerm, searchEngine);

            if (typeof window !== 'undefined') {
                window.open(searchUrl, '_blank', 'noopener,noreferrer');
            }

            customAnalytics.ui_action({
                action: 'Look Up Click',
                screen: screenName || 'MessagesList',
                engine: searchEngine?.name || 'default'
            });
        } catch (error) {
            console.error('Error handling message lookup:', error);
            showDefaultNotification(`lookup-failed-${message.id}-${Date.now()}`, 'Unable to look up message');
        }
    };

    const openLookupSettings = event => {
        event.stopPropagation();
        closeMenuOnAction();
        openSettingsModal(SETTINGS_MODAL_TAB.PROFILE, {
            focusSearchEngine: true
        });
    };

    const showInFolder = event => {
        event.stopPropagation();
        closeMenuOnAction();
        onShowInFolder(message);
    };

    const sendMessageToSlack = async (event) => {
        event.stopPropagation();
        closeMenuOnAction();

        if (sendMessageToSlackLoading || !preferredSlackIntegration) {
            return;
        }

        try {
            const variables = {
                data: {
                    messageId: mutationMessageId,
                    ...(preferredSlackIntegration?.teamId ? { teamId: preferredSlackIntegration.teamId } : {})
                }
            };

            await sendMessageToSlackMutation({ variables });

            const workspaceLabel = preferredSlackIntegration?.workspaceName
                ? lnT('messages:toSlack', { value1: String(preferredSlackIntegration.workspaceName) })
                : lnT('messages:toSlack_3e27fdae');
            showDefaultNotification(`send-to-slack-${message.id}`, `Sent${workspaceLabel}`);
            customAnalytics.message_action({
                action: 'Send to Slack',
                screen: screenName
            }, message);
        } catch (error) {
            const errorMessage = error?.message?.replace(/^GraphQL error:\s*/i, '') || lnT('messages:unableToSendToSlack');
            showDefaultNotification(`send-to-slack-error-${message.id}`, errorMessage);
        }
    };

    const sendMessageToTelegram = async (event, preferredMedia = 'AUTO') => {
        event.stopPropagation();
        closeMenuOnAction();

        if (sendMessageToTelegramLoading || !telegramIntegration?.id) {
            return;
        }

        try {
            await sendMessageToTelegramMutation({
                variables: {
                    data: {
                        messageId: mutationMessageId,
                        preferredMedia
                    }
                }
            });

            const successLabel =
                preferredMedia === 'IMAGE'
                    ? lnT('messages:sentImageToTelegram')
                    : preferredMedia === 'FILE'
                      ? lnT('messages:sentFileToTelegram')
                      : lnT('messages:sentToTelegram');
            showDefaultNotification(`send-to-telegram-${message.id}`, successLabel);
            customAnalytics.message_action({
                action:
                    preferredMedia === 'IMAGE'
                        ? 'Send image to Telegram'
                        : preferredMedia === 'FILE'
                          ? 'Send file to Telegram'
                          : 'Send to Telegram',
                screen: screenName
            }, message);
        } catch (error) {
            const errorMessage = error?.message?.replace(/^GraphQL error:\s*/i, '') || lnT('messages:unableToSendToTelegram');
            showDefaultNotification(`send-to-telegram-error-${message.id}`, errorMessage);
        }
    };

    const sendMessageAction = (action, message, index, extraProperties = {}) => {
        let actionObject = {}
        if (folderFilterType && folderFilterType.length > 0) {
            actionObject["filters_active"] = [...folderFilterType];
        }
        if (searchFolderFilterType && searchFolderFilterType.length > 0) {
            actionObject["search_filters_active"] = [...searchFolderFilterType];
        }
        if (messagesSearchInput && messagesSearchInput.length > 0) {
            actionObject["search_term_length"] = messagesSearchInput.length;
        }
        if (searchGlobal) {
            actionObject["search_global"] = true;
        }
        if (searchDeletedMessages) {
            actionObject["search_trashed"] = true;
        }
        if (showDeletedMessages) {
            actionObject["show_trashed"] = true;
        }

        actionObject = {
            "action": action,
            "screen": screenName,
            "index_position": index,
            ...actionObject,
            ...extraProperties
        }
        console.log('Dropdown select', actionObject)
        customAnalytics.message_action(actionObject, message);
    }

    const selectMessage = (message, listIndex) => {
        if (typeof onSelectMessage === 'function') {
            onSelectMessage(message, listIndex);
            sendMessageAction('Select', message, listIndex);
            return;
        }

        if (selectedMessagesMap[message.id]) {
            let newSpread = [...messagesToSend];
            let index = newSpread.findIndex(obj => obj.id === message.id);

            if (index > -1) {
                newSpread.splice(index, 1);
            }

            setMessagesToSend([...newSpread]);

            delete selectedMessagesMap[message.id];
            setSelectedMessagesMap({
                ...selectedMessagesMap
            });
            sendMessageAction('Deselect', message, listIndex)
        } else {
            setSelectedMessagesMap({
                ...selectedMessagesMap,
                [message.id]: true
            });
            setMessagesToSend([...messagesToSend, message]);
            sendMessageAction('Select', message, listIndex)
        }
    };

    const openEditMessage = (event) => {
        if (event) {
            event.stopPropagation();
        }
        closeMenuOnAction();
        if (typeof onEditMessage === 'function') {
            onEditMessage({
                ...message,
                previewMode: 'MESSAGE'
            });
        } else {
            setSelectedMessage({
                ...message,
                previewMode: 'MESSAGE'
            });
        }
        sendMessageAction('Edit', message, index);
    };

    const openReplyMessage = (event, isQuickAction = false) => {
        if (event) {
            event.stopPropagation();
        }
        closeMenuOnAction();
        if (typeof onReplyMessage === 'function') {
            onReplyMessage(message);
        }
        sendMessageAction(
            'Reply',
            message,
            index,
            isQuickAction ? { quick_action: true, action_surface: 'hover_toolbar' } : undefined
        );
    };

    const [setMessageDueMutation, { loading: setMessageReminderLoading }] = useMutation(MUTATION.SET_MESSAGE_DUE);
    const [clearMessageDueMutation, { loading: clearMessageReminderLoading }] = useMutation(MUTATION.CLEAR_MESSAGE_DUE);

    const openReminderPicker = event => {
        event.stopPropagation();
        closeMenuOnAction();
        setShowReminderPicker(true);
    };

    const copySharedMessageToInbox = async event => {
        event.stopPropagation();
        closeMenuOnAction();

        if (!canCopySharedMessage) {
            return;
        }

        try {
            const response = await copySharedMessageMutation({
                variables: {
                    data: {
                        messageId: mutationMessageId
                    }
                }
            });

            const copiedMessage = response?.data?.copySharedMessage?.message;
            if (!copiedMessage?.id) {
                throw new Error('Message was not copied');
            }

            showDefaultNotification(
                `copy-shared-message-${message.id}-${Date.now()}`,
                lnT('folders:savedToYourInboxAsAnEditable')
            );
            customAnalytics.message_action({
                action: 'Save shared message copy',
                source: 'MessageDropdown'
            }, message);
        } catch (error) {
            const errorMessage = error?.message?.replace(/^GraphQL error:\s*/i, '') || lnT('messages:unableToSaveACopy');
            showDefaultNotification(`copy-shared-message-error-${message.id}-${Date.now()}`, errorMessage);
        }
    };

    const submitReminder = async ({ reminderAt, alerts, reminderMessage }) => {
        try {
            const response = await setMessageDueMutation({
                variables: {
                    input: {
                        messageId: mutationMessageId,
                        dueAt: reminderAt.toISOString(),
                        dueTimezone: getReminderDueTimezone(),
                        alerts,
                        reminderMessage,
                        installationId: REMINDER_WEB_INSTALLATION_ID
                    }
                }
            });

            const updatedMessage = response?.data?.setMessageDue;
            if (!updatedMessage?.id) {
                throw new Error('Reminder was not saved');
            }

            broadcastMessageUpdated(updatedMessage);
            setShowReminderPicker(false);
            showDefaultNotification(
                buildReminderNotificationId('set-reminder'),
                `Reminder set for ${formatReminderTimestamp(updatedMessage.reminderAt)} via ${formatReminderDeliveryTypeLabel(updatedMessage.reminderDeliveryType)}`
            );
            sendMessageAction('Set reminder', message, index);
            customAnalytics.reminder_action({
                'action': 'Set',
                'delivery_type': alerts?.[0]?.deliveryType,
                'lead_hours': Math.round((reminderAt.getTime() - Date.now()) / 3600000),
                'has_note': Boolean(String(reminderMessage || '').trim()),
                'alert_count': alerts?.length || 0,
                'source': 'MessageDropdown'
            });
        } catch (error) {
            const errorMessage = error?.message?.replace(/^GraphQL error:\s*/i, '') || lnT('messages:unableToSaveReminder');
            showDefaultNotification(buildReminderNotificationId('set-reminder-error'), errorMessage);
        }
    };

    const clearReminder = async (event) => {
        event.stopPropagation();
        closeMenuOnAction();

        try {
            const response = await clearMessageDueMutation({
                variables: {
                    messageId: mutationMessageId
                }
            });

            const updatedMessage = response?.data?.clearMessageDue;
            if (!updatedMessage?.id) {
                throw new Error('Reminder was not cleared');
            }

            broadcastMessageUpdated(updatedMessage);
            showDefaultNotification(buildReminderNotificationId('clear-reminder'), 'Reminder cleared');
            sendMessageAction('Clear reminder', message, index);
            customAnalytics.reminder_action({
                'action': 'Clear',
                'source': 'MessageDropdown'
            });
        } catch (error) {
            const errorMessage = error?.message?.replace(/^GraphQL error:\s*/i, '') || lnT('messages:unableToClearReminder');
            showDefaultNotification(buildReminderNotificationId('clear-reminder-error'), errorMessage);
        }
    };

    const removeSelectedMessages = () => {
        setSelectedMessagesMap({});
        setMessagesToSend([]);
        setShowMultiSelect(false);
    };

    const renderStarred = () => {
        return (
            <li>
                <Element
                    onClick={e => {
                        e.stopPropagation();
                        closeMenuOnAction();
                        if (!message.isStarred) {
                            if (!canMutateMessage) return;
                            starMessage({
                                variables: {
                                    data: {
                                        messageId: mutationMessageId
                                    }
                                }
                            });
                        } else {
                            if (!canMutateMessage) return;
                            unstarMessage({
                                variables: {
                                    data: {
                                        messageId: mutationMessageId
                                    }
                                }
                            });
                        }
                    }}
                >
                    <MenuItemLabel>
                        <MenuItemIcon>
                            <FiStar size={14} />
                        </MenuItemIcon>
                        {!message.isStarred ? lnT('messages:star') : lnT('messages:unstar')}
                    </MenuItemLabel>
                </Element>
            </li>
        );
    };

    const renderCheck = () => {
        return (
            <li>
                <Element
                    onClick={e => {
                        e.stopPropagation();
                        closeMenuOnAction();
                        if (!message.isCheckbox) {
                            if (!canMutateMessage) return;
                            addCheckbox({
                                variables: {
                                    data: {
                                        messageId: mutationMessageId
                                    }
                                }
                            });
                        } else {
                            if (!canMutateMessage) return;
                            removeCheckbox({
                                variables: {
                                    data: {
                                        messageId: mutationMessageId
                                    }
                                }
                            });
                        }
                    }}
                >
                    <MenuItemLabel>
                        <MenuItemIcon>
                            {!message.isCheckbox ? <FiCheckSquare size={14} /> : <FiMinusSquare size={14} />}
                        </MenuItemIcon>
                        {!message.isCheckbox ? lnT('messages:addTask') : lnT('messages:untask')}
                    </MenuItemLabel>
                </Element>
            </li>
        );
    };

    const downloadImage = async (image) => {
        try {
            const { url, public_id, displayName, name, size, cdn_url, thumbnail } = image;
            const fileDisplayName = displayName || name || 'image';
            const isCloudinaryImage = isCloudinaryMedia(image);

            let downloadUrl;

            if (isCloudinaryImage) {
                downloadUrl = await cloudinaryCore.url(url);
            } else if (name) {
                const { data, error } = await refetchDownloadFile({
                    data: {
                        fileName: name
                    }
                });

                if (error) {
                    throw error;
                }

                if (data?.downloadFile?.__typename === 'S3File') {
                    downloadUrl = data.downloadFile.url;
                } else {
                    downloadUrl = thumbnail?.original || cdn_url || url;
                }
            } else {
                downloadUrl = thumbnail?.original || cdn_url || url;
            }

            const saved = await saveUrl(downloadUrl, fileDisplayName);
            if (!saved) {
                return;
            }
        } catch (error) {
            console.log('error downloading image', error);
            return;
        }
        customAnalytics.ui_action({
            'action': "Download Image",
            "size": image.size
        });
    };

    const downloadFile = async (file) => {
        try {
            const { url, displayName, name, size, cdn_url } = file;
            const fileDisplayName = resolveDownloadFileName(name, displayName);

            let downloadUrl = cdn_url || url;
            if (name) {
                const { data, error } = await refetchDownloadFile({
                    data: {
                        fileName: name
                    }
                });

                if (error) {
                    throw error;
                }

                if (data?.downloadFile?.__typename === 'S3File') {
                    downloadUrl = data.downloadFile.url;
                }
            }

            const saved = await saveUrl(downloadUrl, fileDisplayName);
            if (!saved) {
                return;
            }
        } catch (error) {
            console.log('error downloading file', error);
            return;
        }
        customAnalytics.ui_action({
            'action': "Download File",
            "size": file.size
        });
    };

    const renderImageDownload = () => {
        if (message.image) {
            return (
                <li>
                    <Element role="menuitem" tabIndex={0}
                        onClick={e => {
                            e.stopPropagation();
                            closeMenuOnAction();
                            downloadImage(message.image);
                        }}
                    >
                        <MenuItemLabel>
                            <MenuItemIcon>
                                <FiImage size={14} />
                            </MenuItemIcon>{lnT('messages:downloadImage_3ac575dc')}</MenuItemLabel>
                    </Element>
                </li>
            );
        }
    };

    const renderFileDownload = () => {
        if (message.file) {
            return (
                <li>
                    <Element role="menuitem" tabIndex={0}
                        onClick={e => {
                            e.stopPropagation();
                            closeMenuOnAction();
                            downloadFile(message.file);
                        }}
                    >
                        <MenuItemLabel>
                            <MenuItemIcon>
                                <FiDownload size={14} />
                            </MenuItemIcon>{lnT('messages:downloadFile')}</MenuItemLabel>
                    </Element>
                </li>
            );
        }
    };

    const executeTrash = () => {
        removeSelectedMessages();
        if (selectedMessage?.id && String(message?.id || '') === String(selectedMessage.id)) {
            setSelectedMessage(undefined);
        }
        trashMessages([message]);
        showDefaultNotification(message.id, 'Messages moved to trash');
    };

    const trashAll = () => {
        if (message.reminderAt) {
            setShowReminderTrashWarning(true);
            return;
        }
        executeTrash();
    };

    const restoreMessageMutation = () => {
        // onDeselect();
        removeSelectedMessages();
        restoreMessagesMutation([message]);
    };

    const toolbarTop = typeof toolbarTopOverride === 'number' ? toolbarTopOverride : -18;
    const shouldRenderFloatingToolbar =
        !hide && !disabled && (hovering || showDropMenu || showReminderPicker || showReminderTrashWarning);

    const [referenceElement, setReferenceElement] = useState(null);
    const [popperElement, setPopperElement] = useState(null);
    const [arrowElement, setArrowElement] = useState(null);
    const { styles, attributes } = usePopper(referenceElement, popperElement, {
        strategy: 'fixed',
        placement: 'bottom-end',
        modifiers: [
            {
                name: 'offset',
                options: {
                    offset: [0, 4]
                }
            },
            { name: 'arrow', options: { element: arrowElement } }
        ]
    });

    const isOtherMenuOpen = Boolean(activeMenuKey && activeMenuKey !== menuKey);
    const shouldHideToolbar = Boolean(isOtherMenuOpen || showSharing);

    // The sharing portal must outlive card hover: entering its backdrop clears hover.
    if (shouldRenderFloatingToolbar || showSharing) {
        return (
            <React.Fragment>
                <HoverToolbar
                    data-message-hover-toolbar
                    data-message-interactive="true"
                    $hidden={shouldHideToolbar}
                    $menuOpen={showDropMenu}
                    $top={toolbarTop}
                    $rightAlign={toolbarRightAlign}
                    $compactMoreOnly={compactMoreOnly}
                    onDoubleClick={event => {
                        event.stopPropagation();
                        event.preventDefault();
                    }}
                >
                    {compactMoreOnly ? (
                        <ToolbarButton
                            ref={setReferenceElement}
                            role="button"
                            data-message-interactive="true"
                            onClick={showMenu}
                            onDoubleClick={event => {
                                event.stopPropagation();
                                event.preventDefault();
                            }}
                            title={lnT('folders:moreActions')}
                            $compactMoreOnly
                            $isActive={showDropMenu}
                        >
                            <FiMoreVertical size={16} color={WHITE} />
                        </ToolbarButton>
                    ) : !message.deletedAt ? (
                        <>
                            {canReplyToMessage ? (
                                <ToolbarButton
                                    role="button"
                                    data-message-interactive="true"
                                    title={lnT('messages:reply')}
                                    onClick={event => openReplyMessage(event, true)}
                                    onDoubleClick={event => {
                                        event.stopPropagation();
                                        event.preventDefault();
                                    }}
                                >
                                    <FiCornerUpLeft size={16} color={WHITE} />
                                </ToolbarButton>
                            ) : null}
                            {typeof showMoveMessageModal === 'function' ? (
                                <ToolbarButton
                                    role="button"
                                    data-message-interactive="true"
                                    title={lnT('common:moveTo')}
                                    onClick={e => {
                                        e.stopPropagation();
                                        closeMenuOnAction();
                                        sendMessageAction('Move', message, index, {
                                            quick_action: true,
                                            action_surface: 'hover_toolbar'
                                        });
                                        showMoveMessageModal(true);
                                    }}
                                    onDoubleClick={event => {
                                        event.stopPropagation();
                                        event.preventDefault();
                                    }}
                                >
                                    <FiFolder size={16} color={WHITE} />
                                </ToolbarButton>
                            ) : null}
                        </>
                    ) : (
                        <ToolbarButton
                            role="button"
                            data-message-interactive="true"
                            title={lnT('messages:restore')}
                            onClick={e => {
                                e.stopPropagation();
                                closeMenuOnAction();
                                restoreMessageMutation();
                            }}
                            onDoubleClick={event => {
                                event.stopPropagation();
                                event.preventDefault();
                            }}
                        >
                            <FiRotateCcw size={16} color={WHITE} />
                        </ToolbarButton>
                    )}
                    {!compactMoreOnly ? (
                        <ToolbarButton
                            ref={setReferenceElement}
                            role="button"
                            data-message-interactive="true"
                            onClick={showMenu}
                            onDoubleClick={event => {
                                event.stopPropagation();
                                event.preventDefault();
                            }}
                            title={lnT('folders:moreActions')}
                            $isActive={showDropMenu}
                        >
                            <FiMoreVertical size={16} color={WHITE} />
                        </ToolbarButton>
                    ) : null}
                </HoverToolbar>
                {showDropMenu ? createPortal(
                    <DropDownMenu
                        ref={setPopperElement}
                        data-message-interactive="true"
                        onClick={event => event.stopPropagation()}
                        onDoubleClick={event => {
                            event.stopPropagation();
                            event.preventDefault();
                        }}
                        style={{ ...styles.popper, zIndex: MESSAGE_MENU_Z_INDEX, maxHeight: 'calc(100vh - 24px)', overflowY: 'auto' }}
                        {...attributes.popper}
                    >
                        <div role="menu" style={{ width: 200 }}>
                            <div role="content">
                                <ul>
                                    {!message.deletedAt ? (
                                        <>
                                            <li>
                                                <Element
                                                    data-message-select
                                                    onClick={e => {
                                                        e.stopPropagation();
                                                        closeMenuOnAction();
                                                        selectMessage(message, index);
                                                    }}
                                                >
                                                    <MenuItemLabel>
                                                        <MenuItemIcon>
                                                            <FiCheck size={14} />
                                                        </MenuItemIcon>{lnT('messages:select')}</MenuItemLabel>
                                                </Element>
                                            </li>
                                            <li>
                                                <Element onClick={openEditMessage}>
                                                    <MenuItemLabel>
                                                        <MenuItemIcon>
                                                            <FiEdit2 size={14} />
                                                        </MenuItemIcon>{lnT('common:edit')}</MenuItemLabel>
                                                </Element>
                                            </li>
                                            {typeof showMoveMessageModal === 'function' ? (
                                                <li>
                                                    <Element
                                                        onClick={event => {
                                                            event.stopPropagation();
                                                            closeMenuOnAction();
                                                            sendMessageAction('Move', message, index);
                                                            showMoveMessageModal(true);
                                                        }}
                                                    >
                                                        <MenuItemLabel>
                                                            <MenuItemIcon>
                                                                <FiFolder size={14} />
                                                            </MenuItemIcon>{lnT('common:moveTo')}</MenuItemLabel>
                                                    </Element>
                                                </li>
                                            ) : null}
                                            <li>
                                                <Element onClick={openReminderPicker}>
                                                    <MenuItemLabel>
                                                        <MenuItemIcon>
                                                            <FiBell size={14} />
                                                        </MenuItemIcon>
                                                        {setMessageReminderLoading
                                                            ? lnT('messages:savingReminder')
                                                            : message?.reminderAt
                                                              ? lnT('messages:changeReminder')
                                                              : lnT('messages:setReminder')}
                                                    </MenuItemLabel>
                                                </Element>
                                            </li>
                                            {message?.reminderAt ? (
                                                <li>
                                                    <Element onClick={clearReminder}>
                                                        <MenuItemLabel>
                                                            <MenuItemIcon>
                                                                <FiXCircle size={14} />
                                                            </MenuItemIcon>
                                                            {clearMessageReminderLoading
                                                                ? lnT('messages:clearingReminder')
                                                                : lnT('messages:clearReminder')}
                                                        </MenuItemLabel>
                                                    </Element>
                                                </li>
                                            ) : null}
                                            {canMutateMessage && !isSharedFolderView && Boolean(meData?.me?.id) && String(message.owner?.id || message.owner) === String(meData?.me?.id) ? (
                                                <li><Element onClick={event => {
                                                    event.stopPropagation();
                                                    closeMenuOnAction();
                                                    setShowSharing(true);
                                                }}><MenuItemLabel><MenuItemIcon><LuMessageSquareShare size={14} /></MenuItemIcon>{lnT('common:shareAndPublish')}</MenuItemLabel></Element></li>
                                            ) : null}
                                            <MessageCopyMenu
                                                message={message}
                                                onCopy={text => {
                                                    closeMenuOnAction();
                                                    copyTextToClipboard(text);
                                                }}
                                            />
                                            {message.image && message.file ? (
                                                <CascadingSubmenu label={lnT('messages:download')} icon={<FiDownload size={14} />}>
                                                    {renderImageDownload()}
                                                    {renderFileDownload()}
                                                </CascadingSubmenu>
                                            ) : (
                                                <>{renderImageDownload()}{renderFileDownload()}</>
                                            )}
                                            {canReplyToMessage || message.title || canSendToTelegram || canSendToSlack || canShowInFolder || canCopySharedMessage ? (
                                                <CascadingSubmenu label={lnT('messages:more')} icon={<FiMoreVertical size={14} />}>
                                                    {canReplyToMessage ? (
                                                        <li>
                                                            <Element role="menuitem" tabIndex={0} onClick={openReplyMessage}>
                                                                <MenuItemLabel>
                                                                    <MenuItemIcon>
                                                                        <FiCornerUpLeft size={14} />
                                                                    </MenuItemIcon>{lnT('messages:reply')}</MenuItemLabel>
                                                            </Element>
                                                        </li>
                                                    ) : null}
                                                    {message.title ? (
                                                        <li>
                                                            <Element role="menuitem" tabIndex={0} onClick={lookupMessageOnWeb}>
                                                                <MenuItemLabel>
                                                                    <MenuItemIcon>
                                                                        <FiSearch size={14} />
                                                                    </MenuItemIcon>{lnT('messages:lookUp_504101bc')}</MenuItemLabel>
                                                                <MenuItemSecondaryAction
                                                                    type="button"
                                                                    title={lnT('messages:lookupSettings')}
                                                                    aria-label={lnT('messages:openLookupSettings')}
                                                                    onClick={openLookupSettings}
                                                                >
                                                                    <FiSettings size={15} />
                                                                </MenuItemSecondaryAction>
                                                            </Element>
                                                        </li>
                                                    ) : null}
                                                    {canChooseTelegramMedia ? (
                                                        <>
                                                            <li>
                                                                <Element role="menuitem" tabIndex={0} onClick={event => sendMessageToTelegram(event, 'IMAGE')}>
                                                                    <MenuItemLabel>
                                                                        <MenuItemIcon>
                                                                            <FiImage size={14} />
                                                                        </MenuItemIcon>
                                                                        {sendMessageToTelegramLoading ? lnT('messages:sendingImageToTelegram') : lnT('messages:sendImageToTelegram')}
                                                                    </MenuItemLabel>
                                                                </Element>
                                                            </li>
                                                            <li>
                                                                <Element role="menuitem" tabIndex={0} onClick={event => sendMessageToTelegram(event, 'FILE')}>
                                                                    <MenuItemLabel>
                                                                        <MenuItemIcon>
                                                                            <FiFileText size={14} />
                                                                        </MenuItemIcon>
                                                                        {sendMessageToTelegramLoading ? lnT('messages:sendingFileToTelegram') : lnT('messages:sendFileToTelegram')}
                                                                    </MenuItemLabel>
                                                                </Element>
                                                            </li>
                                                        </>
                                                    ) : null}
                                                    {canSendToTelegram && !canChooseTelegramMedia ? (
                                                        <li>
                                                            <Element role="menuitem" tabIndex={0} onClick={event => sendMessageToTelegram(event, 'AUTO')}>
                                                                <MenuItemLabel>
                                                                    <MenuItemIcon>
                                                                        <FiMessageSquare size={14} />
                                                                    </MenuItemIcon>
                                                                    {sendMessageToTelegramLoading ? lnT('messages:sendingToTelegram') : lnT('messages:sendToTelegram')}
                                                                </MenuItemLabel>
                                                            </Element>
                                                        </li>
                                                    ) : null}
                                                    {canShowInFolder ? (
                                                        <li>
                                                            <Element role="menuitem" tabIndex={0} onClick={showInFolder}>
                                                                <MenuItemLabel>
                                                                    <MenuItemIcon>
                                                                        <LuMessageSquareShare size={14} />
                                                                    </MenuItemIcon>{lnT('messages:showInFolder_da478765')}</MenuItemLabel>
                                                            </Element>
                                                        </li>
                                                    ) : null}
                                                    {canCopySharedMessage ? (
                                                        <li>
                                                            <Element role="menuitem" tabIndex={0} onClick={copySharedMessageToInbox}>
                                                                <MenuItemLabel>
                                                                    <MenuItemIcon>
                                                                        <FiCopy size={14} />
                                                                    </MenuItemIcon>
                                                                    {copySharedMessageLoading ? lnT('messages:savingCopy') : lnT('messages:saveToMyInbox')}
                                                                </MenuItemLabel>
                                                            </Element>
                                                        </li>
                                                    ) : null}
                                                    {canSendToSlack ? (
                                                        <li>
                                                            <Element role="menuitem" tabIndex={0} onClick={sendMessageToSlack}>
                                                                <MenuItemLabel>
                                                                    <MenuItemIcon>
                                                                        <FiMessageSquare size={14} />
                                                                    </MenuItemIcon>
                                                                    {sendMessageToSlackLoading ? lnT('messages:sendingToSlack') : lnT('messages:sendToSlack')}
                                                                </MenuItemLabel>
                                                            </Element>
                                                        </li>
                                                    ) : null}
                                                </CascadingSubmenu>
                                            ) : null}
                                        </>
                                    ) : (
                                        <li>
                                            <Element
                                                onClick={e => {
                                                    e.stopPropagation();
                                                    closeMenuOnAction();
                                                    selectMessage(message, index);
                                                }}
                                            >
                                                <MenuItemLabel>
                                                    <MenuItemIcon>
                                                        <FiCheck size={14} />
                                                    </MenuItemIcon>{lnT('messages:select')}</MenuItemLabel>
                                            </Element>
                                        </li>
                                    )}
                                    <SeparatorLI />
                                    {message.deletedAt ? (
                                        <li>
                                            <Element
                                                onClick={e => {
                                                    e.stopPropagation();
                                                    closeMenuOnAction();
                                                    showDeleteModal(true);
                                                }}
                                            >
                                                <MenuItemLabel>
                                                    <MenuItemIcon destructive>
                                                        <FiXCircle size={14} />
                                                    </MenuItemIcon>
                                                    <DestructiveLabel>{lnT('folders:deletePermanently_280b10c8')}</DestructiveLabel>
                                                </MenuItemLabel>
                                            </Element>
                                        </li>
                                    ) : (
                                        <li>
                                            <Element
                                                onClick={e => {
                                                    e.stopPropagation();
                                                    closeMenuOnAction();
                                                    trashAll();
                                                }}
                                            >
                                                <MenuItemLabel>
                                                    <MenuItemIcon destructive>
                                                        <FiTrash2 size={14} />
                                                    </MenuItemIcon>
                                                    <DestructiveLabel>{lnT('folders:trash')}</DestructiveLabel>
                                                </MenuItemLabel>
                                            </Element>
                                        </li>
                                    )}
                                </ul>
                            </div>
                        </div>
                    </DropDownMenu>, document.body
                ) : null}
            <DeleteModal
                isOpen={showReminderTrashWarning}
                title={lnT('messages:thisMessageHasAReminder')}
                description={lnT('messages:trashingThisMessageWillCancelTheReminder', { value1: String(formatReminderTimestamp(message.reminderAt)) })}
                closeButtonText={lnT('messages:keepIt')}
                actionButtonText={lnT('folders:trashAnyway')}
                closeModal={(confirmed) => {
                    setShowReminderTrashWarning(false);
                    if (confirmed) {
                        executeTrash();
                    }
                }}
            />
            {showSharing && (
                <React.Suspense fallback={null}>
                    <MessageShareModal key={mutationMessageId} isOpen message={{ ...message, id: mutationMessageId }}
                        closeModal={() => setShowSharing(false)} />
                </React.Suspense>
            )}
            <ReminderPickerModal
                isOpen={showReminderPicker}
                initialReminderAt={effectiveReminderTimestamp}
                initialReminderFiredAt={message?.reminderFiredAt}
                initialDeliveryType={message?.reminderDeliveryType}
                initialReminderMessage={message?.reminderMessage}
                initialAlerts={message?.alerts}
                loading={setMessageReminderLoading}
                onClose={() => setShowReminderPicker(false)}
                onSubmit={submitReminder}
            />
            </React.Fragment>
        );
    } else {
        return null;
    }
}

const HoverToolbar = styled.div`
    visibility: ${props => props.$hidden ? 'hidden' : 'visible'};
    pointer-events: ${props => props.$hidden ? 'none' : 'auto'};
    display: flex;
    align-items: center;
    gap: 2px;
    position: absolute;
    top: ${props => props.$top ?? -18}px;
    right: ${props => props.$rightAlign ? (props.$compactMoreOnly ? '0' : '8px') : 'auto'};
    left: ${props => props.$rightAlign ? 'auto' : 'clamp(0px, calc(100% - 220px), 640px)'};
    background-color: ${props => props.$compactMoreOnly ? 'transparent' : 'rgb(35, 39, 42)'};
    border-radius: ${props => props.$compactMoreOnly ? '999px' : '8px'};
    border: ${props => props.$compactMoreOnly ? 'none' : '1px solid rgba(255, 255, 255, 0.16)'};
    box-shadow: ${props => props.$compactMoreOnly ? 'none' : '0 14px 30px rgba(0, 0, 0, 0.35)'};
    isolation: isolate;
    padding: ${props => props.$compactMoreOnly ? '0' : '4px'};
    z-index: 10000;
`;

const ToolbarButton = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    width: 28px;
    height: 28px;
    border-radius: ${props => props.$compactMoreOnly ? '999px' : '6px'};
    background-color: ${props => {
        if (props.$isActive) {
            return props.$compactMoreOnly ? 'rgba(35, 39, 42, 0.95)' : 'rgba(50, 56, 61, 0.95)';
        }
        return props.$compactMoreOnly ? 'rgba(12, 14, 16, 0.72)' : 'transparent';
    }};
    backdrop-filter: ${props => props.$compactMoreOnly ? 'blur(10px)' : 'none'};
    -webkit-backdrop-filter: ${props => props.$compactMoreOnly ? 'blur(10px)' : 'none'};
    cursor: pointer;
    color: white;
    &:hover {
        background-color: rgba(50, 56, 61, 0.88);
    }
`;

export default MessageDropdown;
